<template>
  <div>
    <div v-if="!isCameraRunning">
      <label class="form-label dz-head"><b>{{ $t('zoneSettings.editor.heading') }}</b></label>
      <p class="dz-note dz-draw-only">
        <u>{{ $t('zoneSettings.editor.important') }}</u> {{ $t('zoneSettings.editor.drawNote') }}
      </p>
      <!-- Shown only on small screens (drawing tools are hidden there) -->
      <div class="dz-small-msg">
        <i class="bi bi-info-circle"></i>
        <span>
          {{ $t('zoneSettings.editor.smallScreenA') }} <i class="bi bi-gear-fill"></i> {{ $t('zoneSettings.editor.smallScreenB') }}
        </span>
      </div>
    </div>

    <!-- Drawing mode banner -->
    <div v-if="!isCameraRunning && drawMode" class="draw-banner dz-draw-only">
      <i :class="drawMode === 'polygon' ? 'bi bi-bounding-box-circles' : 'bi bi-arrow-left-right'"></i>
      <span>
        {{ $t('zoneSettings.editor.drawing') }}
        <b>{{ drawMode === 'polygon' ? $t('zoneSettings.editor.polygonWord') : $t('zoneSettings.editor.tripwireWord') }}</b>
        {{ $t('zoneSettings.editor.for') }}
        <b>{{ activeZoneLabel }}</b>
        <template v-if="drawMode === 'polygon'"> — {{ $t('zoneSettings.editor.polygonHint') }}</template>
        <template v-else> — {{ $t('zoneSettings.editor.tripwireHint') }}</template>
      </span>
      <div class="draw-banner-actions">
        <button class="icon-btn" @click="cancelDrawing" :title="$t('zoneSettings.editor.cancelDrawing')">
          <i class="bi bi-x-lg"></i>
        </button>
      </div>
    </div>

    <!-- image + canvas -->
    <div class="frame-container dz-draw-only">
      <div v-if="capturedFrame">
        <div
          class="frame-content"
          @click="handleFrameClick"
          :class="{ disabled: isCameraRunning, active: !!drawMode }"
        >
          <div v-if="isLoading" class="center">
            <div class="loader"></div>
          </div>
          <img
            v-else
            :src="capturedFrame"
            :alt="$t('zoneSettings.editor.imageAlt')"
            ref="imageRef"
            @load="adjustCanvasSize"
          />
          <canvas
            id="zoneCanvas"
            width="800"
            height="600"
            class="canvas"
          ></canvas>

          <!-- Polygon points being drawn -->
          <div
            v-for="(dot, index) in polygonDrawingPoints"
            :key="'pdot-' + index"
            class="dot"
            :style="{ top: dot.y + 'px', left: dot.x + 'px' }"
          />
          <!-- Tripwire points being drawn -->
          <div
            v-for="(dot, index) in tripwireDrawingPoints"
            :key="'tdot-' + index"
            class="dot tripwire-dot"
            :style="{ top: dot.y + 'px', left: dot.x + 'px' }"
          />
        </div>
      </div>

      <div v-else class="fallback">
        {{ $t('zoneSettings.editor.noSnapshot') }}
      </div>
    </div>

    <button
      class="btn-outline mt dz-draw-only"
      @click="newPicture"
      :disabled="isCameraRunning"
      :title="isCameraRunning ? $t('zoneSettings.editor.stopCamera') : (detectionZones.length > 0 ? $t('zoneSettings.editor.clearFirst') : $t('zoneSettings.editor.takePicture'))"
    >
      {{ $t('zoneSettings.editor.takePicture') }}
    </button>

    <!-- zones table -->
    <div class="zone-grid">
      <div class="zone-row header">
        <span>{{ $t('zoneSettings.editor.colType') }}</span>
        <span>{{ $t('zoneSettings.editor.colName') }}</span>
        <span>{{ $t('zoneSettings.editor.colGeometry') }}</span>
        <span>{{ $t('zoneSettings.editor.colActions') }}</span>
      </div>

      <div v-for="(zone, index) in detectionZones" :key="index" class="zone-row">
        <!-- Editing: name + type become editable (shared with the demo test page). -->
        <div v-if="editingExistingZoneIndex === index" class="identity-cell">
          <ZoneIdentityFields
            v-model:name="zone.name"
            v-model:type="zone.type"
            :original-name="editSnapshot?.name ?? null"
            :original-type="editSnapshot?.type ?? null"
            :type-options="zoneTypeOptions"
            :taken-names="detectionZones.filter((_z, i) => i !== index).map((z) => z.name)"
            @validity="identityValid = $event"
          />
        </div>
        <template v-else>
          <div>
            <span :style="{ color: detectionZoneColorsSolid[zone.type] || '#25323a' }"><b>{{ zoneTypeLabel(zone.type) }}</b></span>
          </div>

          <div>
            <span :style="{ color: detectionZoneColorsSolid[zone.type] || '#25323a' }">{{ zone.name }}</span>
          </div>
        </template>

        <!-- Geometry column: read-only chips when not editing; interactive when in edit mode -->
        <div class="geometry-cell">
          <template v-if="editingExistingZoneIndex === index">
            <button
              v-if="allowPolygonGeometry(zone)"
              class="geom-chip"
              :class="{ 'is-set': hasPolygon(zone), 'is-active': isEditingGeometry(index, 'polygon') }"
              :title="hasPolygon(zone) ? $t('zoneSettings.editor.editPolygon') : $t('zoneSettings.editor.drawPolygon')"
              @click="startGeometryDraw(index, 'polygon')"
            >
              <i class="bi bi-bounding-box-circles"></i>
              <span class="chip-label">{{ $t('zoneSettings.editor.polygon') }}</span>
              <i v-if="hasPolygon(zone)" class="bi bi-check2 status-icon"></i>
            </button>
            <!-- One chip per existing tripwire on this zone -->
            <button
              v-for="(_tw, twIdx) in (zone.tripwires || [])"
              :key="'tw-' + twIdx"
              class="geom-chip"
              :class="{ 'is-set': true, 'is-active': isEditingTripwire(index, twIdx) }"
              :title="$t('zoneSettings.editor.editTripwire', { n: twIdx + 1 })"
              @click="startGeometryDraw(index, 'tripwire', twIdx)"
            >
              <i class="bi bi-arrow-left-right"></i>
              <span class="chip-label">
                {{ $t('zoneSettings.editor.tripwire') }}<span v-if="(zone.tripwires || []).length > 1"> {{ twIdx + 1 }}</span>
              </span>
              <i class="bi bi-check2 status-icon"></i>
            </button>
            <!-- Append-new chip -->
            <button
              class="geom-chip add-chip"
              :class="{ 'is-active': isEditingTripwire(index, null) }"
              :title="(zone.tripwires || []).length === 0 ? $t('zoneSettings.editor.drawTripwire') : $t('zoneSettings.editor.addAnotherTripwire')"
              @click="startGeometryDraw(index, 'tripwire', null)"
            >
              <i class="bi bi-plus-lg"></i>
              <span class="chip-label">
                {{ (zone.tripwires || []).length === 0 ? $t('zoneSettings.editor.tripwire') : $t('zoneSettings.editor.addTripwire') }}
              </span>
            </button>
          </template>
          <template v-else>
            <span v-if="allowPolygonGeometry(zone)" class="geom-chip readonly" :class="{ 'is-set': hasPolygon(zone) }">
              <i class="bi bi-bounding-box-circles"></i>
              <span class="chip-label">{{ $t('zoneSettings.editor.polygon') }}</span>
              <i v-if="hasPolygon(zone)" class="bi bi-check2 status-icon"></i>
            </span>
            <span class="geom-chip readonly" :class="{ 'is-set': hasTripwire(zone) }">
              <i class="bi bi-arrow-left-right"></i>
              <span class="chip-label">
                <template v-if="tripwireCount(zone) >= 2">{{ $t('zoneSettings.editor.tripwireGroup', { n: tripwireCount(zone) }) }}</template>
                <template v-else>{{ $t('zoneSettings.editor.tripwire') }}</template>
              </span>
              <i v-if="hasTripwire(zone)" class="bi bi-check2 status-icon"></i>
            </span>
          </template>
        </div>

        <div class="actions">
          <!-- Edit-mode actions -->
          <template v-if="editingExistingZoneIndex === index">
            <button
              class="icon-btn"
              @click="clearActiveDrawingPoints"
              :disabled="!drawMode || !hasActiveDrawingPoints"
              :title="$t('zoneSettings.editor.clearPoints')"
            >
              <i class="bi bi-eraser-fill"></i>
            </button>
            <button
              class="icon-btn ok"
              @click="confirmExistingZoneEdit()"
              :disabled="!identityValid"
              :title="$t('zoneSettings.editor.confirmChanges')"
            >
              <i class="bi bi-check-lg"></i>
            </button>
            <button
              class="icon-btn"
              @click="cancelExistingZoneEdit(index)"
              :title="$t('zoneSettings.editor.cancelDiscard')"
            >
              <i class="bi bi-x-lg"></i>
            </button>
          </template>

          <!-- Default actions -->
          <template v-else>
            <button class="icon-btn" @click="openSettings(index)" :disabled="isCameraRunning" :title="$t('zoneSettings.editor.settings')">
              <i class="bi bi-gear-fill"></i>
            </button>
            <button
              class="icon-btn dz-draw-only"
              @click="startEditingExistingZone(index)"
              :disabled="isCameraRunning || editingExistingZoneIndex !== null"
              :title="$t('zoneSettings.editor.editIdentity')"
            >
              <i class="bi bi-pencil-fill"></i>
            </button>
            <button class="icon-btn danger dz-draw-only" @click="deleteZone(index)" :disabled="isCameraRunning" :title="$t('zoneSettings.editor.deleteZone')">
              <i class="bi bi-trash-fill"></i>
            </button>
          </template>
        </div>
      </div>

      <!-- New zone row -->
      <div v-if="!isCameraRunning" class="zone-row dz-draw-only">
        <div class="cell-center">
          <DropdownDetectionZoneType :key="newZone.type" v-model="newZone.type" @detectionzonetype="on_detectionzonetype" />
        </div>
        <div class="cell-center">
          <input type="text" class="input" v-model="newZone.name" :placeholder="$t('zoneSettings.editor.zoneNamePlaceholder')" />
        </div>

        <div class="geometry-cell">
          <button
            v-if="allowPolygonGeometry(newZone)"
            class="geom-chip"
            :class="{ 'is-set': hasPolygon(newZone), 'is-active': isEditingNewZone() && drawMode === 'polygon' }"
            :disabled="!newZone.type || !newZone.name"
            :title="hasPolygon(newZone) ? $t('zoneSettings.editor.editPolygon') : $t('zoneSettings.editor.drawPolygon')"
            @click="startGeometryDraw(null, 'polygon')"
          >
            <i class="bi bi-bounding-box-circles"></i>
            <span class="chip-label">{{ $t('zoneSettings.editor.polygon') }}</span>
            <i v-if="hasPolygon(newZone)" class="bi bi-check2 status-icon"></i>
          </button>
          <!-- One chip per tripwire already drawn on the new zone -->
          <button
            v-for="(_tw, twIdx) in (newZone.tripwires || [])"
            :key="'newtw-' + twIdx"
            class="geom-chip"
            :class="{ 'is-set': true, 'is-active': isEditingTripwire(null, twIdx) }"
            :disabled="!newZone.type || !newZone.name"
            :title="$t('zoneSettings.editor.editTripwire', { n: twIdx + 1 })"
            @click="startGeometryDraw(null, 'tripwire', twIdx)"
          >
            <i class="bi bi-arrow-left-right"></i>
            <span class="chip-label">
              {{ $t('zoneSettings.editor.tripwire') }}<span v-if="(newZone.tripwires || []).length > 1"> {{ twIdx + 1 }}</span>
            </span>
            <i class="bi bi-check2 status-icon"></i>
          </button>
          <!-- Append-new chip -->
          <button
            class="geom-chip add-chip"
            :class="{ 'is-active': isEditingTripwire(null, null) }"
            :disabled="!newZone.type || !newZone.name"
            :title="(newZone.tripwires || []).length === 0 ? $t('zoneSettings.editor.drawTripwire') : $t('zoneSettings.editor.addAnotherTripwire')"
            @click="startGeometryDraw(null, 'tripwire', null)"
          >
            <i class="bi bi-plus-lg"></i>
            <span class="chip-label">
              {{ (newZone.tripwires || []).length === 0 ? $t('zoneSettings.editor.tripwire') : $t('zoneSettings.editor.addTripwire') }}
            </span>
          </button>
        </div>

        <div class="actions">
          <button
            class="icon-btn"
            v-if="isEditingNewZone()"
            @click="clearActiveDrawingPoints"
            :disabled="!hasActiveDrawingPoints"
            :title="$t('zoneSettings.editor.clearPoints')"
          >
            <i class="bi bi-eraser-fill"></i>
          </button>
          <button
            class="icon-btn ok"
            @click="finalizeNewZone"
            :disabled="!canFinalizeNewZone"
            :title="$t('zoneSettings.editor.confirmZone')"
          >
            <i class="bi bi-check-lg"></i>
          </button>
        </div>
      </div>
    </div>

    <!-- settings popup -->
    <div v-if="isSettingsPopupVisible" class="modal-overlay">
      <detection-zone-settings
        :flowId="selectedFlowId"
        :detectionZone="selectedZone"
        :defaultSettings="defaultSettings"
        :popUpTrigger="popUpTrigger"
        :detectionZoneColorsSolid="detectionZoneColorsSolid"
        :capturedFrame="capturedFrame"
        @close="closeSettings"
        @updateZone="updateZone"
      />
    </div>
  </div>
</template>

<script>
import axios from 'axios'

import { ref, computed, watch } from 'vue'

import { useToast } from 'vue-toastification'

import { detection_zone_colors_by_name, detection_zone_colors_by_name_solid } from '@/constants.js'

import { useCompanyStore } from '@/stores/CompanyStore'
import { parseDeviceUrl } from '@/api/deviceUrl'

import DropdownDetectionZoneType from './DropdownDetectionZoneType.vue'
import DetectionZoneSettings from './DetectionZoneSettings.vue'
import ZoneIdentityFields from './zones/ZoneIdentityFields.vue'
import { useDetectionZoneTypeStore } from '@/stores/DetectionZoneTypeStore'
import { i18n } from '@/i18n'
import { zoneTypeLabel } from '@/i18n/zoneTypes'

export default {
    name: 'DetectionZone',

    components: {
      DropdownDetectionZoneType,
      DetectionZoneSettings,
      ZoneIdentityFields
    },

    emits: ["detectionzones"],

    props: {
      stream_link: String,
      // The camera stream's ID, for /detection_zone_frame. The edge resolves the
      // URL itself now; stream_link stays because the template still shows it.
      stream_id: Number,
      captured_frame: String,
      detection_zone: Object,
      is_camera_running: Boolean,
      flow_id:Number,
    },

    setup(props, { emit }) {
      const toast = useToast()

      const companyStore = useCompanyStore()

      const capturedFrame = ref(props.captured_frame)
      const isCameraRunning = ref(props.is_camera_running)

      // Confirmed zones (each may carry a polygon, a tripwire, or both)
      const detectionZones = ref([])

      // The "new zone" being authored at the bottom of the grid
      const newZone = ref({ type: '', name: '', points: [], tripwires: [], tripwire: null })

      // Drawing state. drawMode === null → editor inactive (clicks on canvas do nothing).
      // editingZoneIndex === null + drawMode set → drawing on the new zone row.
      // editingZoneIndex === <number> → drawing on an existing confirmed zone.
      // editingTripwireIndex: only meaningful when drawMode === 'tripwire'.
      //   null → appending a new tripwire to the target zone's list
      //   <number> → editing the tripwire at that index in the list
      const drawMode = ref(null)                  // 'polygon' | 'tripwire' | null
      const editingZoneIndex = ref(null)
      const editingTripwireIndex = ref(null)
      const polygonDrawingPoints = ref([])        // [{x, y}]
      const tripwireDrawingPoints = ref([])       // [{x, y}] max 2

      // Geometry-edit mode for an existing confirmed zone.
      // editingExistingZoneIndex === <number> → that zone has the Edit button toggled on.
      // editSnapshot stores the original geometry so Cancel can revert.
      const editingExistingZoneIndex = ref(null)
      const editSnapshot = ref(null)

      // Renaming / retyping an existing zone (ZoneIdentityFields). The save sends the
      // zone's DetectionZoneId along, so the server keeps it the same zone.
      const zoneTypeStore = useDetectionZoneTypeStore()
      if (!(zoneTypeStore.detectionzonetypes || []).length) zoneTypeStore.fetchDetectionZoneTypes()
      const zoneTypeOptions = computed(() =>
        (zoneTypeStore.detectionzonetypes || []).map((t) => t.DetectionZoneTypeName))
      const identityValid = ref(true)

      const streamlink = ref(props.stream_link)

      const imageRef = ref(null)

      const isSettingsPopupVisible = ref(false)
      const selectedFlowId = ref(null)
      const selectedZone = ref({})
      const popUpTrigger = ref('')

      const defaultSettings = {
        // The portal's link: a zone ID, stable across renames.
        "LinkedDetectionZoneId": null,
        // The same link as a NAME. Written for the edge firmware, which reads this
        // field; kept in step with the id by the settings modal. Remove once the edge
        // reads the id.
        "ConnectedInteractionZoneNames": null,
        "DetectionOverlapMode":"bottom_overlap",
        "BoundingBoxOverlapPercentage":0.55,
        "BottomOverlapPercentage":0.55,
        "DebounceWritingThreshold":2,
        "ThresholdRemoveId": 10,
        // SplitVisitMergeThreshold / ReIDMergeThresholdSec /
        // MaxPlausibleVisitDurationSec are deliberately ABSENT. The nightly
        // matcher resolves a per-zone-type default for each whenever the key is
        // missing, so omitting them here is what makes a new zone track the
        // platform default instead of freezing today's value into its blob.
        // The settings modal only writes these keys when the user picks
        // "Custom". See frontend/src/utils/zoneMatchingDefaults.js.
        //
        // MinimumDurationThreshold / MaximumDurationThresholdSet /
        // MaximumDurationThreshold were also removed: no backend code ever read
        // them, so the sliders bound to them did nothing.
        "PeopleAmountConvertedToVisits":1,
        "SameIdInOutUniqueData":false,
        "TriggerAction": false,
        "TriggerActionThreshold": 0,
        "ActionZoneCooldown":0,
        "ActionAcknowledgePauseDuration":0,
        // Service-response measurement (docs/reference/service_response_measurement.md).
        // How long it is reasonable for someone to reach this zone — the boundary
        // between "no one came" and "gave up and left". Unlike its three siblings
        // above, 0 is NOT a safe default here: a zero grace makes every unanswered
        // wait "no one came" and the "gave up" bucket can never fill.
        "UnattendedGraceSeconds": 60,
        "EngagersConversionRate": 0.1,
        "AverageProductValue": 0,
        "DetectStandStill":false,
        "DirectionsCombinedEE": { vertical: "down", horizontal: "right", movementPattern: "diagonal" },
        // Phase 1 — Dwell Group (Mode C): opt-in flag to estimate average dwell
        // time from this zone's in/out crossings using Little's Law. Only
        // meaningful for tripwire-bearing zones; the editor gates the toggle
        // accordingly. Default off — explicit opt-in.
        "UseTripwireForDwell": false
      }

      const isLoading = ref(false)

      // ---------- helpers -------------------------------------------------

      const hasPolygon = (zone) => Array.isArray(zone?.points) && zone.points.length >= 3

      const isValidTripwire = (tw) =>
        tw && Array.isArray(tw.points) && tw.points.length === 2

      // Stable per-tripwire id. Direction config in DetectionZoneSettings.TripwireDirections
      // keys on these — they have to survive add / delete / reorder edits. Frontend
      // generates them; backend preserves them on save and round-trips them on load.
      const generateTripwireId = () =>
        `tw_${Math.random().toString(16).slice(2, 10).padStart(8, '0')}`

      // Returns the array of valid tripwires for a zone (always an array, possibly empty).
      const zoneTripwires = (zone) =>
        Array.isArray(zone?.tripwires) ? zone.tripwires.filter(isValidTripwire) : []

      const tripwireCount = (zone) => zoneTripwires(zone).length

      // True if the zone has at least one valid tripwire. Used by FlowSettings /
      // DetectionZoneSettings gating logic that originally cared about a single tripwire.
      const hasTripwire = (zone) => tripwireCount(zone) >= 1

      // Keep zone.tripwire (legacy single-tripwire field) in sync with tripwires[0],
      // so existing readers (FlowSettings.vue, DetectionZoneSettings.vue) keep working.
      const syncLegacyTripwire = (zone) => {
        if (!zone) return
        const tws = zoneTripwires(zone)
        zone.tripwire = tws.length > 0 ? tws[0] : null
      }

      // Entrance / Exit / Combined EE zones are tripwire-only for new authoring.
      // Legacy zones that already carry a polygon stay editable (so the user can
      // refine the polygon or migrate to a tripwire), but a freshly created E/E/EE
      // zone never gets the option to draw a polygon — we want to nudge everyone
      // toward the tripwire workflow for these types.
      const ENTRANCE_LIKE_TYPES = ['Entrance Zone', 'Exit Zone', 'Combined Entrance Exit Zone']
      const isEntranceLikeType = (type) => ENTRANCE_LIKE_TYPES.includes(type)
      const allowPolygonGeometry = (zone) =>
        !isEntranceLikeType(zone?.type) || hasPolygon(zone)

      const isEditingZone = (index) => drawMode.value && editingZoneIndex.value === index
      const isEditingNewZone = () => drawMode.value && editingZoneIndex.value === null
      const isEditingGeometry = (index, geometry) =>
        drawMode.value === geometry && editingZoneIndex.value === index
      const isEditingTripwire = (zoneIndex, tripwireIndex) =>
        drawMode.value === 'tripwire' &&
        editingZoneIndex.value === zoneIndex &&
        editingTripwireIndex.value === tripwireIndex

      const activeZoneLabel = computed(() => {
        if (editingZoneIndex.value === null) return newZone.value.name || i18n.global.t('zoneSettings.editor.newZone')
        return detectionZones.value[editingZoneIndex.value]?.name || i18n.global.t('zoneSettings.editor.zone')
      })

      const hasActiveDrawingPoints = computed(() => {
        if (drawMode.value === 'polygon') return polygonDrawingPoints.value.length > 0
        if (drawMode.value === 'tripwire') return tripwireDrawingPoints.value.length > 0
        return false
      })

      const canConfirmActiveDrawing = computed(() => {
        if (drawMode.value === 'polygon') return polygonDrawingPoints.value.length >= 3
        if (drawMode.value === 'tripwire') return tripwireDrawingPoints.value.length === 2
        return false
      })

      const canFinalizeNewZone = computed(() => {
        if (!newZone.value.type || !newZone.value.name) return false
        // The active drawing buffer is auto-committed on save; we just need to know
        // whether the resulting zone will have at least one valid geometry.
        const polyOk = drawMode.value === 'polygon'
          ? polygonDrawingPoints.value.length >= 3
          : hasPolygon(newZone.value)
        // Tripwire is OK if either the new-zone already has a committed tripwire,
        // or the user is mid-draw with a valid 2-point buffer.
        const tripOk =
          hasTripwire(newZone.value) ||
          (drawMode.value === 'tripwire' && tripwireDrawingPoints.value.length === 2)
        return polyOk || tripOk
      })

      // ---------- canvas sizing & redraw ----------------------------------

      const adjustCanvasSize = () => {
        const canvas = document.getElementById('zoneCanvas')
        const img = imageRef.value
        if (img && canvas) {
          canvas.width = img.clientWidth
          canvas.height = img.clientHeight
          redrawAll()
        }
      }

      watch(() => props.captured_frame, (newFrame) => {
        capturedFrame.value = newFrame
        adjustCanvasSize()
      })

      watch(() => props.detection_zone, (newZones) => {
        if (newZones) {
          detectionZones.value = Object.entries(newZones).map(([name, zone]) => {
            const polygonPts = Array.isArray(zone.points)
              ? zone.points.map(([x, y]) => ({ x, y }))
              : []

            // Normalize tripwire data → tripwires array with stable ids.
            // Accepts the current multi-tripwire shape (zone.tripwires:
            // [{id, points: [[x,y],[x,y]]}, ...]) and the legacy single-tripwire
            // shape (zone.tripwire: {id?, points: [[x,y],[x,y]]}). When an id is
            // missing (older payload, older server, drawn-then-not-yet-saved entry)
            // a fresh one is synthesized so internal state always has a key for
            // DetectionZoneSettings.TripwireDirections lookups.
            let tripwires = []
            if (Array.isArray(zone.tripwires)) {
              tripwires = zone.tripwires
                .filter(tw => tw && Array.isArray(tw.points) && tw.points.length === 2)
                .map(tw => ({
                  id: (typeof tw.id === 'string' && tw.id) ? tw.id : generateTripwireId(),
                  points: tw.points.map(([x, y]) => ({ x, y })),
                }))
            } else if (zone.tripwire && Array.isArray(zone.tripwire.points) && zone.tripwire.points.length === 2) {
              const id = (typeof zone.tripwire.id === 'string' && zone.tripwire.id)
                ? zone.tripwire.id
                : generateTripwireId()
              tripwires = [{
                id,
                points: zone.tripwire.points.map(([x, y]) => ({ x, y })),
              }]
            }

            const built = {
              id: zone.DetectionZoneId ?? null,
              name,
              type: zone.type,
              points: polygonPts,
              tripwires,
              tripwire: null, // synced below
              confirmed: true,
              settings: zone.settings || {},
            }
            syncLegacyTripwire(built)
            return built
          })
          adjustCanvasSize()
        }
      }, { immediate: true })

      watch(() => props.stream_link, (newStreamLink) => {
        streamlink.value = newStreamLink
      }, { immediate: true })

      watch(() => props.is_camera_running, (newStatus) => {
        isCameraRunning.value = newStatus
        if (newStatus) cancelDrawing()
        adjustCanvasSize()
      })

      // Re-render the canvas whenever the drawing buffer changes
      watch([polygonDrawingPoints, tripwireDrawingPoints, detectionZones], () => {
        redrawAll()
      }, { deep: true })

      // ---------- emit ---------------------------------------------------

      const emitDetectionPointsData = () => {
        const formattedZones = detectionZones.value.reduce((acc, zone) => {
          const entry = {
            DetectionZoneId: zone.id ?? null,
            type: zone.type,
            points: hasPolygon(zone) ? zone.points.map(p => [p.x, p.y]) : [],
            settings: zone.settings || {},
          }
          const tws = zoneTripwires(zone)
          if (tws.length > 0) {
            // Legacy single-tripwire field (first entry) kept for FlowSettings.vue
            // and other call-sites that still read `zone.tripwire`. Now carries
            // the id too so a server save round-trips it correctly.
            entry.tripwire = {
              id: tws[0].id,
              points: tws[0].points.map(p => [p.x, p.y]),
            }
            entry.tripwires = tws.map(tw => ({
              id: tw.id,
              points: tw.points.map(p => [p.x, p.y]),
            }))
          } else {
            entry.tripwire = null
            entry.tripwires = []
          }
          acc[zone.name] = entry
          return acc
        }, {})
        emit('detectionzones', formattedZones)
      }

      // ---------- type/name handling -------------------------------------

      const on_detectionzonetype = (value) => {
        newZone.value.type = value
      }

      const updateZoneType = (index, value) => {
        if (index < detectionZones.value.length) {
          detectionZones.value[index].type = value
        }
      }

      // ---------- click → add point --------------------------------------

      const handleFrameClick = (event) => {
        if (isCameraRunning.value) return
        if (!drawMode.value) return
        if (event.target !== imageRef.value) return

        const rect = imageRef.value.getBoundingClientRect()
        const x = event.clientX - rect.left
        const y = event.clientY - rect.top

        if (drawMode.value === 'polygon') {
          polygonDrawingPoints.value.push({ x, y })
        } else if (drawMode.value === 'tripwire') {
          if (tripwireDrawingPoints.value.length >= 2) {
            toast.info(i18n.global.t('zoneSettings.editor.tripwireFull'))
            return
          }
          tripwireDrawingPoints.value.push({ x, y })
        }
      }

      // ---------- enter draw mode ----------------------------------------

      // Auto-commit the active drawing buffer to its target zone.
      // Returns true if the buffer was successfully committed (or there was nothing to commit),
      // false if validation blocks the commit.
      // Empty buffer = "remove this geometry from the zone".
      const tryCommitActiveDrawing = () => {
        if (!drawMode.value) return true

        const target = editingZoneIndex.value === null
          ? newZone.value
          : detectionZones.value[editingZoneIndex.value]
        if (!target) return true

        if (drawMode.value === 'polygon') {
          const buf = polygonDrawingPoints.value
          if (buf.length === 0) {
            target.points = []
          } else if (buf.length >= 3) {
            target.points = buf.map(p => ({ ...p }))
          } else {
            toast.warning(i18n.global.t('zoneSettings.editor.polygonMin'))
            return false
          }
        } else if (drawMode.value === 'tripwire') {
          const buf = tripwireDrawingPoints.value
          const twIdx = editingTripwireIndex.value
          if (!Array.isArray(target.tripwires)) target.tripwires = []

          if (buf.length === 0) {
            // Empty buffer commits a "remove" — only meaningful when editing an
            // existing tripwire. Appending mode (twIdx === null) with no points
            // is a no-op (user opened the chip but didn't draw anything).
            if (twIdx !== null && twIdx >= 0 && twIdx < target.tripwires.length) {
              const removed = target.tripwires[twIdx]
              target.tripwires.splice(twIdx, 1)
              // Drop the deleted tripwire's direction setting so it doesn't
              // linger forever in DetectionZoneSettings.TripwireDirections.
              if (removed && removed.id && target.settings &&
                  target.settings.TripwireDirections &&
                  removed.id in target.settings.TripwireDirections) {
                delete target.settings.TripwireDirections[removed.id]
              }
            }
          } else if (buf.length === 2) {
            const points = buf.map(p => ({ ...p }))
            if (twIdx === null) {
              // Appending a brand-new tripwire — give it a fresh id.
              target.tripwires.push({ id: generateTripwireId(), points })
            } else if (twIdx >= 0 && twIdx < target.tripwires.length) {
              // Replacing an existing tripwire — preserve its id so the
              // associated TripwireDirections entry stays attached.
              const existing = target.tripwires[twIdx]
              const id = (existing && existing.id) ? existing.id : generateTripwireId()
              target.tripwires.splice(twIdx, 1, { id, points })
            } else {
              // Defensive: stale index, fall back to append.
              target.tripwires.push({ id: generateTripwireId(), points })
            }
          } else {
            toast.warning(i18n.global.t('zoneSettings.editor.tripwireTwo'))
            return false
          }

          syncLegacyTripwire(target)
        }

        polygonDrawingPoints.value = []
        tripwireDrawingPoints.value = []
        drawMode.value = null
        editingTripwireIndex.value = null

        // Existing zones emit their changes immediately
        const wasExistingZone = editingZoneIndex.value !== null
        editingZoneIndex.value = null
        if (wasExistingZone) emitDetectionPointsData()
        return true
      }

      // tripwireIndex (only meaningful when geometry === 'tripwire'):
      //   null → user clicked the "+ Tripwire" chip → draw a new one and append on commit
      //   <number> → user clicked an existing tripwire chip → load its points for editing
      const startGeometryDraw = (index, geometry, tripwireIndex = null) => {
        if (isCameraRunning.value) return
        // Validate: new-zone needs type+name
        if (index === null && (!newZone.value.type || !newZone.value.name)) {
          toast.warning(i18n.global.t('zoneSettings.editor.pickTypeName'))
          return
        }

        // Toggle off when clicking the same chip you're already drawing on.
        // For tripwires, "same chip" means same zone *and* same tripwire slot.
        if (drawMode.value === geometry && editingZoneIndex.value === index) {
          if (geometry !== 'tripwire' || editingTripwireIndex.value === tripwireIndex) {
            tryCommitActiveDrawing()
            return
          }
          // Different tripwire slot in the same zone → fall through, the auto-commit
          // below handles switching cleanly.
        }

        // Auto-commit any current drawing before switching
        if (drawMode.value && !tryCommitActiveDrawing()) return

        editingZoneIndex.value = index
        drawMode.value = geometry

        // Pre-load existing points into the drawing buffer so the user can amend
        const target = index === null ? newZone.value : detectionZones.value[index]
        if (geometry === 'polygon') {
          polygonDrawingPoints.value = hasPolygon(target)
            ? target.points.map(p => ({ ...p }))
            : []
          tripwireDrawingPoints.value = []
          editingTripwireIndex.value = null
        } else {
          editingTripwireIndex.value = tripwireIndex
          const tws = zoneTripwires(target)
          if (tripwireIndex !== null && tripwireIndex >= 0 && tripwireIndex < tws.length) {
            tripwireDrawingPoints.value = tws[tripwireIndex].points.map(p => ({ ...p }))
          } else {
            tripwireDrawingPoints.value = []
          }
          polygonDrawingPoints.value = []
        }
      }

      // ---------- confirm / clear / cancel drawing -----------------------

      // Kept for backwards compatibility with any callers; same behavior as auto-commit.
      const confirmActiveDrawing = () => {
        tryCommitActiveDrawing()
      }

      // ---------- existing-zone geometry edit mode -----------------------

      const startEditingExistingZone = (index) => {
        if (isCameraRunning.value) return
        if (editingExistingZoneIndex.value !== null && editingExistingZoneIndex.value !== index) {
          // Force-close any other zone that is currently being edited
          if (!confirmExistingZoneEdit(editingExistingZoneIndex.value)) return
        }
        const zone = detectionZones.value[index]
        // Snapshot current geometry for cancel — preserve tripwire ids so a
        // cancel/restore doesn't orphan the matching TripwireDirections entries.
        editSnapshot.value = {
          name: zone.name,
          type: zone.type,
          points: hasPolygon(zone) ? zone.points.map(p => ({ ...p })) : [],
          tripwires: zoneTripwires(zone).map(tw => ({
            id: tw.id,
            points: tw.points.map(p => ({ ...p })),
          }))
        }
        editingExistingZoneIndex.value = index
      }

      const confirmExistingZoneEdit = () => {
        // Commit any pending drawing first (must be valid)
        if (drawMode.value && !tryCommitActiveDrawing()) return false

        // The zone must still have at least one geometry — otherwise block.
        const idx = editingExistingZoneIndex.value
        const zone = idx !== null ? detectionZones.value[idx] : null
        if (!zone || (!hasPolygon(zone) && !hasTripwire(zone))) {
          toast.warning(i18n.global.t('zoneSettings.editor.needGeometryA'))
          return false
        }
        if (!identityValid.value) {
          toast.warning(i18n.global.t('zoneSettings.editor.fixIdentity'))
          return false
        }
        zone.name = zone.name.trim()

        editSnapshot.value = null
        editingExistingZoneIndex.value = null
        emitDetectionPointsData()

        // Open the settings modal so the user can review/adjust settings — same UX as confirming a new zone.
        selectedFlowId.value = props.flow_id
        selectedZone.value = { ...zone }
        popUpTrigger.value = 'ExistingZone'
        isSettingsPopupVisible.value = true
        return true
      }

      const cancelExistingZoneEdit = (index) => {
        // Discard any pending drawing
        cancelDrawing()
        // Restore the snapshot
        if (editSnapshot.value && index !== null && index !== undefined) {
          const zone = detectionZones.value[index]
          if (zone) {
            zone.name = editSnapshot.value.name ?? zone.name
            zone.type = editSnapshot.value.type ?? zone.type
            zone.points = editSnapshot.value.points.map(p => ({ ...p }))
            zone.tripwires = (editSnapshot.value.tripwires || []).map(tw => ({
              id: tw.id,
              points: tw.points.map(p => ({ ...p })),
            }))
            syncLegacyTripwire(zone)
          }
        }
        editSnapshot.value = null
        editingExistingZoneIndex.value = null
      }

      const clearActiveDrawingPoints = () => {
        if (drawMode.value === 'polygon') polygonDrawingPoints.value = []
        else if (drawMode.value === 'tripwire') tripwireDrawingPoints.value = []
      }

      const cancelDrawing = () => {
        polygonDrawingPoints.value = []
        tripwireDrawingPoints.value = []
        drawMode.value = null
        editingZoneIndex.value = null
        editingTripwireIndex.value = null
      }

      // ---------- finalize new zone --------------------------------------

      const finalizeNewZone = () => {
        if (!canFinalizeNewZone.value) return

        // Commit any pending drawing on the new zone before finalizing
        if (drawMode.value && editingZoneIndex.value === null) {
          if (!tryCommitActiveDrawing()) return
        }

        // Re-check after commit (zone must still have at least one geometry)
        if (!hasPolygon(newZone.value) && !hasTripwire(newZone.value)) {
          toast.warning(i18n.global.t('zoneSettings.editor.needGeometryThe'))
          return
        }

        const created = {
          name: newZone.value.name,
          type: newZone.value.type,
          points: hasPolygon(newZone.value) ? newZone.value.points.map(p => ({ ...p })) : [],
          tripwires: zoneTripwires(newZone.value).map(tw => ({
            id: tw.id,
            points: tw.points.map(p => ({ ...p })),
          })),
          tripwire: null, // synced below
          confirmed: true,
          settings: {},
        }
        syncLegacyTripwire(created)
        detectionZones.value.push(created)

        // Reset new-zone authoring buffer
        newZone.value = { type: '', name: '', points: [], tripwires: [], tripwire: null }

        emitDetectionPointsData()

        // Open settings popup so the user can adjust per-zone settings
        selectedFlowId.value = props.flow_id
        selectedZone.value = { ...created }
        popUpTrigger.value = 'NewZone'
        isSettingsPopupVisible.value = true
      }

      // ---------- drawing primitives -------------------------------------

      const drawPoly = (ctx, points, color) => {
        if (points.length < 2) return
        ctx.fillStyle = color
        ctx.strokeStyle = color.replace('0.5', '1')
        ctx.lineWidth = 2

        ctx.beginPath()
        ctx.moveTo(points[0].x, points[0].y)
        points.forEach(point => ctx.lineTo(point.x, point.y))
        if (points.length >= 3) ctx.closePath()
        if (points.length >= 3) ctx.fill()
        ctx.stroke()
      }

      const drawTripwire = (ctx, points, color) => {
        if (points.length < 2) return
        const [a, b] = points
        const stroke = color.replace('0.5', '1')

        ctx.strokeStyle = stroke
        ctx.fillStyle = stroke
        ctx.lineWidth = 4
        ctx.lineCap = 'round'

        // Main line
        ctx.beginPath()
        ctx.moveTo(a.x, a.y)
        ctx.lineTo(b.x, b.y)
        ctx.stroke()

        // Endpoint dots
        ctx.beginPath()
        ctx.arc(a.x, a.y, 5, 0, Math.PI * 2)
        ctx.fill()
        ctx.beginPath()
        ctx.arc(b.x, b.y, 5, 0, Math.PI * 2)
        ctx.fill()
      }

      const redrawAll = () => {
        const canvas = document.getElementById('zoneCanvas')
        if (!canvas) return
        const ctx = canvas.getContext('2d')
        ctx.clearRect(0, 0, canvas.width, canvas.height)

        // Confirmed zones
        detectionZones.value.forEach((zone, idx) => {
          // Skip the geometry currently being edited (drawn from buffer instead)
          const editingThisPolygon = drawMode.value === 'polygon' && editingZoneIndex.value === idx

          const baseColor = detection_zone_colors_by_name[zone.type] || 'rgba(0, 0, 0, 0.5)'
          if (hasPolygon(zone) && !editingThisPolygon) drawPoly(ctx, zone.points, baseColor)

          // Draw every tripwire on this zone except the one currently being edited.
          zoneTripwires(zone).forEach((tw, twIdx) => {
            const editingThisTripwire =
              drawMode.value === 'tripwire' &&
              editingZoneIndex.value === idx &&
              editingTripwireIndex.value === twIdx
            if (!editingThisTripwire) drawTripwire(ctx, tw.points, baseColor)
          })
        })

        // New zone (not yet confirmed) — render committed geometries
        if (newZone.value) {
          const baseColor = detection_zone_colors_by_name[newZone.value.type] || 'rgba(120, 120, 120, 0.5)'
          const editingNewPolygon = drawMode.value === 'polygon' && editingZoneIndex.value === null
          if (hasPolygon(newZone.value) && !editingNewPolygon) drawPoly(ctx, newZone.value.points, baseColor)

          zoneTripwires(newZone.value).forEach((tw, twIdx) => {
            const editingThisTripwire =
              drawMode.value === 'tripwire' &&
              editingZoneIndex.value === null &&
              editingTripwireIndex.value === twIdx
            if (!editingThisTripwire) drawTripwire(ctx, tw.points, baseColor)
          })
        }

        // Live drawing buffer
        if (drawMode.value === 'polygon' && polygonDrawingPoints.value.length) {
          const targetType = editingZoneIndex.value === null
            ? newZone.value?.type
            : detectionZones.value[editingZoneIndex.value]?.type
          const color = detection_zone_colors_by_name[targetType] || 'rgba(120, 120, 120, 0.5)'
          drawPoly(ctx, polygonDrawingPoints.value, color)
        }
        if (drawMode.value === 'tripwire' && tripwireDrawingPoints.value.length) {
          const targetType = editingZoneIndex.value === null
            ? newZone.value?.type
            : detectionZones.value[editingZoneIndex.value]?.type
          const color = detection_zone_colors_by_name[targetType] || 'rgba(120, 120, 120, 0.5)'
          drawTripwire(ctx, tripwireDrawingPoints.value, color)
        }
      }

      // ---------- delete / settings popup --------------------------------

      const deleteZone = (index) => {
        if (confirm(i18n.global.t('zoneSettings.editor.confirmDelete'))) {
          detectionZones.value.splice(index, 1)
          if (editingZoneIndex.value === index) cancelDrawing()
          else if (editingZoneIndex.value !== null && editingZoneIndex.value > index) {
            editingZoneIndex.value -= 1
          }
          emitDetectionPointsData()
        }
      }

      const openSettings = (index) => {
        selectedFlowId.value = props.flow_id
        selectedZone.value = { ...detectionZones.value[index] }
        popUpTrigger.value = 'ExistingZone'
        isSettingsPopupVisible.value = true
      }

      const updateZone = (updatedZone) => {
        // Match by name (and optionally points). Settings updates apply to whichever zone matches the name.
        const index = detectionZones.value.findIndex(zone => zone.name === updatedZone.name)
        if (index !== -1) {
          detectionZones.value[index] = {
            ...detectionZones.value[index],
            settings: updatedZone.settings,
            confirmed: true,
          }
        }
        emitDetectionPointsData()
      }

      const closeSettings = () => {
        isSettingsPopupVisible.value = false
        popUpTrigger.value = ''
      }

      // ---------- frame capture ------------------------------------------

      const newPicture = async () => {
        if (isCameraRunning.value) return

        if (props.stream_id === undefined || props.stream_id === null) {
          toast.error(i18n.global.t('zoneSettings.editor.noStream'))
          return
        }

        isLoading.value = true
        try {
          const { httpBase, proxied } = parseDeviceUrl(companyStore.company.SocketioDeviceURL)

          const response = await axios.get(`${httpBase}/detection_zone_frame`, {
            params: { cameraStreamId: props.stream_id },
            responseType: 'blob',
            withCredentials: proxied
          })
          const blob = new Blob([response.data], { type: 'image/jpeg' })
          const urlCreator = window.URL || window.webkitURL
          const imageUrl = urlCreator.createObjectURL(blob)
          capturedFrame.value = imageUrl
          toast.success(i18n.global.t('zoneSettings.editor.frameCaptured'))
        } catch (error) {
          console.error('Frame capture failed:', error)
          toast.error(i18n.global.t('zoneSettings.editor.frameFailed', { error: error.message }))
        } finally {
          isLoading.value = false
        }
      }

      return {
        capturedFrame,
        isCameraRunning,
        on_detectionzonetype,
        updateZoneType,
        newZone,
        imageRef,
        polygonDrawingPoints,
        tripwireDrawingPoints,
        handleFrameClick,
        newPicture,
        detectionZones,
        isLoading,
        deleteZone,
        isSettingsPopupVisible,
        selectedFlowId,
        selectedZone,
        defaultSettings,
        openSettings,
        updateZone,
        closeSettings,
        popUpTrigger,
        adjustCanvasSize,
        detectionZoneColorsSolid: detection_zone_colors_by_name_solid,

        // tripwire-aware additions
        drawMode,
        editingZoneIndex,
        editingTripwireIndex,
        editingExistingZoneIndex,
        hasPolygon,
        hasTripwire,
        tripwireCount,
        allowPolygonGeometry,
        isEditingZone,
        isEditingNewZone,
        isEditingGeometry,
        isEditingTripwire,
        activeZoneLabel,
        hasActiveDrawingPoints,
        canConfirmActiveDrawing,
        canFinalizeNewZone,
        startGeometryDraw,
        confirmActiveDrawing,
        clearActiveDrawingPoints,
        cancelDrawing,
        finalizeNewZone,
        startEditingExistingZone,
        confirmExistingZoneEdit,
        cancelExistingZoneEdit,
        editSnapshot,
        zoneTypeOptions,
        identityValid,
        zoneTypeLabel,
    }
  }
}

</script>

<style scoped>
.dz-head{ margin-top:10px; margin-bottom:8px; color:#425461 }
.dz-note{ font-size:.9rem; color:#C21500; margin-top:0 }

.draw-banner{
  display:flex; align-items:center; gap:10px;
  margin: 8px 0;
  padding: 8px 12px;
  border:1px solid #4F7EB3;
  background:#f8fbff;
  color:#425461;
  border-radius:10px;
  font-size:.9rem;
}
.draw-banner i{ color:#4F7EB3 }
.draw-banner-actions{ margin-left:auto; display:flex; gap:6px }

.frame-container{ max-width:100%; overflow:hidden; padding-top:10px }
.frame-content{ position:relative; max-width:100%; max-height:1000px; overflow:auto; cursor:default }
.frame-content.active{ cursor:crosshair }
.frame-content img{ width:auto; height:auto; max-width:none; max-height:none }
.frame-content.disabled { cursor: not-allowed; }
.canvas{ position:absolute; top:0; left:0; pointer-events:none }
.center{ display:flex; align-items:center; justify-content:center; padding:40px 0 }
.fallback{ padding:40px 0; text-align:center; color:#999 }

.dot{
  position:absolute; width:6px; height:6px; background:#e63946; border-radius:50%;
  transform:translate(-50%, -50%);
}
.dot.tripwire-dot{
  width:10px; height:10px; background:#4F7EB3;
  box-shadow:0 0 0 2px #fff;
}

/* Grid */
.zone-grid{
  display:grid; grid-template-columns: 1.4fr 1.5fr 1.6fr 1.4fr; gap:0; margin-top:18px;
  border:1px solid #eef3f7; border-radius:12px; overflow:hidden;
}
.zone-row{ display:contents }
.zone-row > .identity-cell{ grid-column: span 2 }
.zone-row > *{ padding:10px 12px; border-bottom:1px solid #eef3f7; background:#fff }
.zone-row:last-child > *{ border-bottom:none }

.header > *{
  background:#f8fbff; color:#4F7EB3; font-weight:800; border-bottom:1px solid #e6edf3;
}

/* Inputs + actions */
.input{
  width:100%; border:1px solid #dfe7ee; border-radius:10px; padding:8px 10px;
  background:#fff; color:#2b3a44; outline:none;
}
.input:focus{ border-color:#4F7EB3; box-shadow:0 0 0 3px rgba(79,126,179,.12) }

.actions{ display:flex; gap:8px; align-items:center; flex-wrap:wrap }
.icon-btn{
  display:inline-flex; align-items:center; justify-content:center;
  border:1px solid #e6edf3; background:#fff; color:#6C9046;
  width:34px; height:34px; border-radius:8px; cursor:pointer;
}
.icon-btn:hover{ background:#f4f9ff }
.icon-btn.danger{ color:#C21500 }
.icon-btn.ok{ color:#fff; background:#6C9046; border-color:#6C9046 }
.icon-btn.ok:disabled{ background:#dde6dd; color:#fff; border-color:#dde6dd }
.icon-btn:disabled{ opacity:.45; cursor:not-allowed }

/* Geometry chips */
/* Keeps the type dropdown / name input at their natural height (vertically
   centered) when the geometry chips wrap and make the row taller, instead of
   stretching the controls to the full row height. */
.cell-center{ display:flex; align-items:center; }

.geometry-cell{
  display:flex; gap:8px; flex-wrap:wrap; align-items:center;
}
.geom-chip{
  display:inline-flex; align-items:center; gap:6px;
  padding:6px 10px; border-radius:999px;
  background:#f4f7fb; border:1px solid #e6edf3; color:#7a8a96;
  cursor:pointer; font-size:.85rem; font-weight:600;
}
.geom-chip:hover:not(:disabled){ background:#eaf1f8; color:#4F7EB3 }
.geom-chip.is-set{ background:#eaf3e2; color:#3f6b16; border-color:#cfe1bb }
.geom-chip.is-set:hover{ background:#dfeed1 }
.geom-chip.is-active{
  background:#fff7e0; color:#a96a00; border-color:#f3d98a;
  box-shadow:0 0 0 3px rgba(243,217,138,.35);
}
.geom-chip:disabled{ opacity:.5; cursor:not-allowed }
.geom-chip .chip-label{ font-weight:700 }
.geom-chip .status-icon{ color:#3f6b16 }
.geom-chip.readonly{ cursor:default; opacity:.85 }
.geom-chip.readonly:hover{ background:#f4f7fb; color:#7a8a96 }
.geom-chip.readonly.is-set:hover{ background:#eaf3e2; color:#3f6b16 }

/* "+ Tripwire" / "+ Add tripwire" chip — visually distinct from the set/active chips */
.geom-chip.add-chip{
  background:#ffffff; color:#4F7EB3; border-style:dashed;
}
.geom-chip.add-chip:hover:not(:disabled){
  background:#eaf1f8; color:#4F7EB3;
}

/* Stack the zones table into cards below 850px so the 4 fr-columns don't clip
   on the right in the narrow band where the sidebar is still shown (~768–810).
   Labels come from nth-child; ::before uses flex-basis:100% so it sits ABOVE
   the chips/buttons in the flex cells and above the text in the plain cells. */
@media (max-width: 850px){
  .zone-grid{ display:block; border:0; border-radius:0; overflow:visible; }
  .zone-row{
    display:block; border:1px solid #eef3f7; border-radius:12px;
    margin-bottom:10px; background:#fff; overflow:hidden;
  }
  .zone-row.header{ display:none; }
  .zone-row > *{ border-bottom:1px dashed #eef3f7; padding:8px 12px; }
  .zone-row > *:last-child{ border-bottom:0; }
  .zone-row > *::before{
    display:block; flex-basis:100%;
    font-weight:700; color:#4F7EB3; font-size:.72rem;
    text-transform:uppercase; letter-spacing:.4px; margin-bottom:4px;
  }
  .zone-row > *:nth-child(1)::before{ content:"Type"; }
  .zone-row > *:nth-child(2)::before{ content:"Name"; }
  .zone-row > *:nth-child(3)::before{ content:"Geometry"; }
  .zone-row > *:nth-child(4)::before{ content:"Actions"; }
}

/* Take new picture */
.btn-outline{
  display:inline-flex; align-items:center; gap:8px; margin-top:16px;
  border:1px solid #4F7EB3; color:#4F7EB3; background:#fff;
  padding:8px 12px; border-radius:10px; cursor:pointer; text-decoration:none;
}
.btn-outline:hover{ background:#4F7EB3; color:#fff }

.btn-outline:disabled,
.btn-outline[disabled] {
  opacity: .55;
  cursor: not-allowed;
  background: #f0f4f8;
  color: #9aa8b4;
  border-color: #d3dbe3;
}

.btn-outline:disabled:hover,
.btn-outline[disabled]:hover {
  background: #f0f4f8;
  color: #9aa8b4;
}

.mt{ margin-top:16px }

/* Modal */
.modal-overlay{
  position:fixed; inset:0; background:rgba(0,0,0,.5);
  display:flex; align-items:center; justify-content:center; z-index:2000; overflow:hidden;
}

/* ── Small screens (≤1024): drawing zones on a small canvas isn't practical, so
   hide all add/draw tooling and show a notice. The zones list + the ⚙ settings
   button (and its modal) stay available so settings can still be changed. ── */
.dz-small-msg{ display:none; }
@media (max-width: 1024px){
  .dz-draw-only{ display:none !important; }
  .dz-small-msg{
    display:flex; align-items:flex-start; gap:8px;
    padding:10px 12px; margin:8px 0 4px;
    background:#eef3f8; border:1px solid #d1dde9; border-radius:10px;
    color:#37526b; font-size:.9rem; line-height:1.4;
  }
  .dz-small-msg i{ color:#4F7EB3; }
}

/* Loader */
.loader{
  width:60px; padding:8px; aspect-ratio:1; border-radius:50%;
  background:#B2C149;
  --_m: conic-gradient(#0000 10%,#000), linear-gradient(#000 0 0) content-box;
  -webkit-mask:var(--_m); mask:var(--_m);
  -webkit-mask-composite:source-out; mask-composite:subtract;
  animation:spinner 1s linear infinite;
}
@keyframes spinner{ to{ transform:rotate(1turn) } }
</style>
